import React from 'react';
import PropTypes from 'prop-types';
import MediaThumbnail from './MediaThumbnail';
import {LanguageTabs} from '@/components/atoms';
import {ROUTES} from '@/utils/constants/routesConstants';
import {getFileNameFromUrl} from '@/helpers/shadcn/utils';
import useBackgammonGameReview from '@/hooks/useBackgammonGameReview';
import {GameSectionTitle} from './GameReviewComponents';
import {
  GAME_SPACING,
  createLeaderboardDownloadHandler
} from './gameReviewUtils';
import {
  GameReviewLayout,
  GameReviewSetupContent,
  GameReviewAttributesContent,
  EditableAccordionItem,
  GameImageWithFields
} from './GameReviewSharedSections';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';

const formatActivityType = (type) =>
  type
    ? type
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    : '-';

const BackgammonGameReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  isExcluded,
  getFormattedDate
}) => {
  const {downloadFileOnBlob} = useAzureBlob();
  const handleLeaderboardFileDownload =
    createLeaderboardDownloadHandler(downloadFileOnBlob);

  const {
    gameDisplayTabIndex,
    setGameDisplayTabIndex,
    previewModal,
    handlePreview,
    handleClosePreview,
    asset,
    languages,
    languageCodes,
    gameDisplayLangCode,
    gameDisplayTranslation
  } = useBackgammonGameReview(activityFormData);

  const isEmpty =
    !activityFormData || Object.keys(activityFormData).length === 0;

  return (
    <GameReviewLayout
      testId='backgammon-game-review'
      containerClassName='backgammon-game-review-container'
      innerClassName='backgammon-game-review'
      loading={loading}
      showToast={showToast}
      toastMessage={toastMessage}
      handleToastClose={handleToastClose}
      handleViewActivity={handleViewActivity}
      previewModal={previewModal}
      handleClosePreview={handleClosePreview}
      isEmpty={isEmpty}>
      {/* Setup Accordion */}
      <EditableAccordionItem
        label='Setup'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_ADD)}>
        <GameReviewSetupContent
          testIdPrefix='backgammon'
          activityFormData={activityFormData}
          getFormattedDate={getFormattedDate}
          isExcluded={isExcluded}
          formatActivityType={formatActivityType}
        />
      </EditableAccordionItem>

      {/* Display Accordion */}
      <EditableAccordionItem
        label='Display'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_DISPLAY)}>
        <div className='w-full'>
          <GameSectionTitle label='Game Display' className='mb-4' />

          {/* Language Tabs */}
          {languageCodes.length > 0 && (
            <div className='mb-4'>
              <LanguageTabs
                languages={languageCodes}
                fullLanguages={languages}
                activeTabIndex={gameDisplayTabIndex}
                setActiveTabIndex={setGameDisplayTabIndex}
                testIdPrefix='review-game-display-tab'
              />
            </div>
          )}

          {/* Game Image Section */}
          <GameImageWithFields
            title='Game Image'
            imageUrl={asset?.gameImage}
            imageAlt='game-image'
            handlePreview={handlePreview}
            previewTitle='Game Image Preview'
            thumbnailTestId='game-image-thumbnail-row'
            fieldsTestId={`game-display-fields-${gameDisplayLangCode}`}
            fields={[
              {
                label: 'Game Image',
                value: asset?.gameImage
                  ? getFileNameFromUrl(asset.gameImage)
                  : '-'
              },
              {label: 'Name', value: gameDisplayTranslation.title || '-'},
              {
                label: 'Tag Description',
                value: gameDisplayTranslation.tagDescription || '-'
              }
            ]}
          />

          {/* Logo Image Section */}
          <GameImageWithFields
            title='Logo Image'
            className='mb-6 mt-6'
            imageUrl={asset?.logoImage}
            imageAlt='logo-image'
            handlePreview={handlePreview}
            previewTitle='Logo Image Preview'
            thumbnailTestId='logo-image-thumbnail-row'
            fieldsTestId='logo-description-row'
            fields={[
              {
                label: 'Logo Image',
                value: asset?.logoImage
                  ? getFileNameFromUrl(asset.logoImage)
                  : '-'
              },
              {
                label: 'Game Description',
                value: gameDisplayTranslation.gameDescription || '-',
                isHtml: true
              },
              {
                label: 'How to Play Rule',
                value: gameDisplayTranslation.howToPlayRule || '-',
                isHtml: true
              }
            ]}
          />

          {/* Background Images Section */}
          <div className='mb-6 mt-6'>
            <GameSectionTitle label='Background Images' className='mb-4' />

            {/* Row 1: Background Menu | Background Image | Background VS */}
            <div className='flex items-start gap-6'>
              <div className='shrink-0'>
                <p className='mb-2 text-sm text-[#D0D0D0]'>Background Menu</p>
                <MediaThumbnail
                  url={asset?.backgroundMenuImage}
                  alt='background-menu'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundMenuImage,
                      'image',
                      'Background Menu Preview'
                    )
                  }
                />
              </div>

              <div className={`grid flex-1 ${GAME_SPACING.threeColGridClass}`}>
                <div />
                <div>
                  <p className='mb-2 text-sm text-[#D0D0D0]'>
                    Background Image
                  </p>
                  <MediaThumbnail
                    url={asset?.backgroundImage}
                    alt='background-image'
                    type='image'
                    onPreview={() =>
                      handlePreview(
                        asset?.backgroundImage,
                        'image',
                        'Background Image Preview'
                      )
                    }
                  />
                </div>

                <div>
                  <p className='mb-2 text-sm text-[#D0D0D0]'>Background VS</p>
                  <MediaThumbnail
                    url={asset?.backgroundVS}
                    alt='background-vs'
                    type='image'
                    onPreview={() =>
                      handlePreview(
                        asset?.backgroundVS,
                        'image',
                        'Background VS Preview'
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Background Waiting | Background Exit */}
            <div className='mt-6 flex items-start gap-6'>
              <div className='shrink-0'>
                <p className='mb-2 text-sm text-[#D0D0D0]'>
                  Background Waiting
                </p>
                <MediaThumbnail
                  url={asset?.backgroundWaiting}
                  alt='background-waiting'
                  type='image'
                  onPreview={() =>
                    handlePreview(
                      asset?.backgroundWaiting,
                      'image',
                      'Background Waiting Preview'
                    )
                  }
                />
              </div>

              <div className={`grid flex-1 ${GAME_SPACING.threeColGridClass}`}>
                <div />
                <div>
                  <p className='mb-2 text-sm text-[#D0D0D0]'>Background Exit</p>
                  <MediaThumbnail
                    url={asset?.backgroundExit}
                    alt='background-exit'
                    type='image'
                    onPreview={() =>
                      handlePreview(
                        asset?.backgroundExit,
                        'image',
                        'Background Exit Preview'
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </EditableAccordionItem>

      {/* Attributes Accordion */}
      <EditableAccordionItem
        label='Attributes'
        isEditable={isEditable}
        editButtonSchema={editButtonSchema}
        onEdit={() => navigate(ROUTES.ACTIVITY_ATTRIBUTES)}>
        <GameReviewAttributesContent
          activityFormData={activityFormData}
          handleLeaderboardFileDownload={handleLeaderboardFileDownload}
        />
      </EditableAccordionItem>
    </GameReviewLayout>
  );
};

BackgammonGameReview.propTypes = {
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  editButtonSchema: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(BackgammonGameReview);
